import React, { useEffect } from 'react';
import icon_newpost_delete from '../../../../assets/icons/icon_newpost_delete.svg';
import { PostMedia } from '../../../../types/postType';
import { getImageStyles, processMediaFiles, processCameraCapture } from '../../../../services/mediaServices';
import VIImage from '../../../common/VIImage/VIImage';
import './MediaManager.css';

interface MediaManagerProps {
  mediaList: PostMedia[];
  selectedGif: string | null;
  onMediaListChange: (newMediaList: PostMedia[]) => void;
  onGifChange: (gif: string | null) => void;
  onError: (message: string) => void;
  maxItems?: number;
}

const MediaManager: React.FC<MediaManagerProps> = ({
  mediaList,
  selectedGif,
  onMediaListChange,
  onGifChange,
  onError,
  maxItems = 10
}) => {
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    if (mediaList.length + files.length + (selectedGif ? 1 : 0) > maxItems) {
      onError(`You can only add up to ${maxItems} media items`);
      return;
    }

    const { success, errors } = await processMediaFiles(files);
    
    if (errors.length > 0) {
      errors.forEach((error: string) => onError(error));
    }
    
    if (success.length > 0) {
      onMediaListChange([...mediaList, ...success]);
    }
  };

  const handleCameraImage = (imageSrc: string) => {
    if (mediaList.length + (selectedGif ? 1 : 0) >= maxItems) {
      onError(`You can only add up to ${maxItems} media items`);
      return;
    }

    const img = new Image();
    img.onload = () => {
      const newMedia = {
        ...processCameraCapture(imageSrc),
        width: img.width,
        height: img.height
      };
      onMediaListChange([...mediaList, newMedia]);
    };
    img.src = imageSrc;
  };

  const renderMedia = (media: PostMedia, index: number) => {
    if (media.media_type === 'video') {
      return (
        <video 
          src={media.url} 
          controls 
          style={{ width: '176px', height: '176px', objectFit: 'cover' }}
          className="uploaded-media"
        />
      );
    }

    return (
      <VIImage 
        src={media.url} 
        alt={`Media ${index + 1}`} 
        className="uploaded-media"
        style={getImageStyles(media, mediaList.length)}
      />
    );
  };

  return (
    <div className="post-mediaList">
      <div className="uploaded-media-container">
        {mediaList.map((media, index) => (
          <div key={index} className="post-media">
            {renderMedia(media, index)}
            <button 
              className="delete-button" 
              onClick={() => onMediaListChange(mediaList.filter((_, i) => i !== index))}
            >
              <img src={icon_newpost_delete} alt="Delete" />
            </button>
          </div>
        ))}
        {selectedGif && (
          // <div className="post-media">
          <div>
            <VIImage 
              src={selectedGif} 
              alt="Selected GIF" 
              className="uploaded-media"
              style={getImageStyles({ 
                url: selectedGif, 
                media_type: 'gif',
                width: 176,
                height: 176
              }, mediaList.length + 1)}
            />
            <button 
              className="delete-button" 
              onClick={() => onGifChange(null)}
            >
              <img src={icon_newpost_delete} alt="Delete" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface UploadedMediaManagerProps {
  mediaList: PostMedia[];
  selectedGif: string | null;
  onMediaListChange: (newMediaList: PostMedia[]) => void;
  onGifChange: (gif: string | null) => void;
  onError: (message: string) => void;
  maxItems?: number;
}

const UploadedMediaManager: React.FC<UploadedMediaManagerProps> = ({
  mediaList,
  selectedGif,
  onMediaListChange,
  onGifChange
}) => {
  const getAspectRatioStyle = (media: PostMedia) => {
    const ratio = media.width / media.height;
    return {
      height: '178px',
      aspectRatio: ratio.toString(),
      objectFit: 'cover' as const
    };
  };

  return (
    <div className="uploaded-media-list">
      {mediaList.map((media, index) => (
        <div key={index} className="uploaded-media-item">
          {media.media_type === 'video' ? (
            <video 
              src={media.url} 
              controls 
              style={{ height: '178px', width: 'auto' }}
              className="uploaded-media"
            />
          ) : (
            <img 
              src={media.url} 
              alt={`Media ${index + 1}`} 
              className="uploaded-media"
              style={getAspectRatioStyle(media)}
            />
          )}
          <button 
            className="delete-button" 
            onClick={() => onMediaListChange(mediaList.filter((_, i) => i !== index))}
          >
            <img src={icon_newpost_delete} alt="Delete" />
          </button>
        </div>
      ))}
      {selectedGif && (
        <div className="uploaded-media-item">
          <img 
            src={selectedGif} 
            alt="Selected GIF" 
            style={{ height: '178px', width: '178px' }}
            className="uploaded-media"
          />
          <button 
            className="delete-button" 
            onClick={() => onGifChange(null)}
          >
            <img src={icon_newpost_delete} alt="Delete" />
          </button>
        </div>
      )}
    </div>
  );
};

export default UploadedMediaManager;